import React, { useState, useEffect } from 'react';
import { Modal, List, Tag, Button, Space, message, Empty, Spin } from 'antd';
import {
  LaptopOutlined,
  MobileOutlined,
  TabletOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { authApi } from '@/services/api';
import type { Session } from '@/types/auth';

interface SessionsModalProps {
  visible: boolean;
  onClose: () => void;
}

const SessionsModal: React.FC<SessionsModalProps> = ({ visible, onClose }) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      loadSessions();
    }
  }, [visible]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await authApi.getSessions();
      setSessions(response.data);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '加载会话列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTerminateSession = async (sessionId: string) => {
    setTerminatingId(sessionId);
    try {
      await authApi.terminateSession(sessionId);
      message.success('会话已终止');
      loadSessions();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '终止会话失败');
    } finally {
      setTerminatingId(null);
    }
  };

  const handleTerminateAll = () => {
    Modal.confirm({
      title: '确认踢出所有其他会话？',
      content: '此操作将使所有其他设备上的登录失效',
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await authApi.terminateAllSessions();
          message.success('所有其他会话已终止');
          loadSessions();
        } catch (error: any) {
          message.error(error.response?.data?.detail || '操作失败');
        }
      },
    });
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'mobile':
        return <MobileOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
      case 'tablet':
        return <TabletOutlined style={{ fontSize: '24px', color: '#52c41a' }} />;
      default:
        return <LaptopOutlined style={{ fontSize: '24px', color: '#722ed1' }} />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) {
      return '刚刚';
    } else if (diff < 3600) {
      return `${Math.floor(diff / 60)}分钟前`;
    } else if (diff < 86400) {
      return `${Math.floor(diff / 3600)}小时前`;
    } else {
      return `${Math.floor(diff / 86400)}天前`;
    }
  };

  return (
    <Modal
      title="活跃会话"
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
        <Button key="terminate-all" danger onClick={handleTerminateAll} disabled={sessions.length <= 1}>
          踢出所有其他会话
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        {sessions.length === 0 ? (
          <Empty description="暂无活跃会话" />
        ) : (
          <List
            dataSource={sessions}
            renderItem={(session) => (
              <List.Item
                actions={
                  session.is_current
                    ? [<Tag color="blue">当前会话</Tag>]
                    : [
                        <Button
                          danger
                          size="small"
                          loading={terminatingId === session.id}
                          onClick={() => handleTerminateSession(session.id)}
                        >
                          踢出
                        </Button>,
                      ]
                }
              >
                <List.Item.Meta
                  avatar={getDeviceIcon(session.device_type)}
                  title={
                    <Space>
                      <span>
                        {session.device_type === 'mobile'
                          ? '移动设备'
                          : session.device_type === 'tablet'
                          ? '平板设备'
                          : 'PC设备'}
                      </span>
                      <Tag color="processing">{session.login_method}</Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Space>
                        <EnvironmentOutlined />
                        <span style={{ fontSize: '12px' }}>IP: {session.ip_address}</span>
                      </Space>
                      <Space>
                        <ClockCircleOutlined />
                        <span style={{ fontSize: '12px' }}>登录于 {formatDate(session.created_at)}</span>
                      </Space>
                      {session.user_agent && (
                        <div style={{ fontSize: '11px', color: '#999', maxWidth: '400px' }}>
                          {session.user_agent}
                        </div>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </Modal>
  );
};

export default SessionsModal;
